import { useEffect, useRef } from "react";
import { Instagram, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/ui-custom/Reveal";
import { COMPANY, resolveUrl } from "@/lib/api";

const embedUrl = (url) => {
  const m = String(url).match(/\/(reel|reels|p)\/([A-Za-z0-9_-]+)/);
  return m ? `https://www.instagram.com/${m[1] === "p" ? "p" : "reel"}/${m[2]}/embed/` : null;
};

const ReelVideo = ({ src, link, index }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) el.play().catch(() => {}); else el.pause(); }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div className="relative aspect-[9/16] rounded-md overflow-hidden bg-[var(--ink)] group" data-testid={`reel-video-wrap-${index}`}>
      <video ref={ref} src={resolveUrl(src)} muted loop playsInline autoPlay preload="metadata" className="absolute inset-0 h-full w-full object-cover" data-testid={`reel-video-${index}`} />
      {link && (
        <a href={link} target="_blank" rel="noreferrer" className="absolute bottom-4 left-4 right-4 inline-flex items-center justify-center gap-2 bg-[rgba(255,252,242,0.92)] text-[var(--ink)] text-xs font-semibold uppercase tracking-[0.12em] py-3 rounded-full opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300" data-testid={`reel-watch-${index}`}>
          <Instagram size={14} /> Watch on Instagram
        </a>
      )}
    </div>
  );
};

export const InstagramSection = ({ reels = [], videos = [], heading }) => {
  const vids = videos.filter(Boolean).slice(0, 3);
  const embeds = reels.map(embedUrl).filter(Boolean).slice(0, 3);
  if (!vids.length && !embeds.length) return null;
  const cols = Math.max(vids.length || embeds.length, 1);
  return (
    <section className="py-24 lg:py-32 bg-[var(--cream)]" data-testid="instagram-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <SectionHeading eyebrow="On Instagram" title={heading || "Fresh from the deli counter"} description="Behind the scenes at the Slice House, new arrivals and what's landing in stores this week. Follow @deliver_food." />
          <Reveal delay={0.2}>
            <a href={COMPANY.instagram} target="_blank" rel="noreferrer" className="btn-pill btn-outline shrink-0" data-testid="instagram-follow-btn"><Instagram size={14} /> Follow @deliver_food <ArrowUpRight size={14} /></a>
          </Reveal>
        </div>
        <div className={`mt-14 grid gap-6 ${cols === 1 ? "max-w-sm mx-auto" : cols === 2 ? "sm:grid-cols-2 max-w-3xl mx-auto" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="instagram-grid">
          {vids.length
            ? vids.map((v, i) => <Reveal key={v} delay={i * 0.1}><ReelVideo src={v} link={reels[i]} index={i} /></Reveal>)
            : embeds.map((src, i) => (
              <Reveal key={src} delay={i * 0.1}>
                <div className="rounded-md overflow-hidden bg-white border border-[var(--sand)]" data-testid={`instagram-embed-${i}`}>
                  <iframe title={`Deliver Instagram reel ${i + 1}`} src={src} className="w-full block" style={{ height: 620, border: 0 }} loading="lazy" allow="autoplay; encrypted-media" allowFullScreen scrolling="no" />
                </div>
              </Reveal>
            ))}
        </div>
      </div>
    </section>
  );
};
